import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Mail, Save, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { validatePassword } from "@/lib/passwordValidation";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";

export default function Configuracoes() {
  const { toast } = useToast();
  const { fullName, email, isLoading: profileLoading, updateProfile } = useProfile();
  const { updatePassword, verifyPassword } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({ name: "" });

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (fullName !== null) {
      setFormData((prev) => ({ ...prev, name: fullName }));
    }
  }, [fullName]);

  const handleSave = async () => {
    setIsSaving(true);
    const success = await updateProfile(formData.name);
    setIsSaving(false);

    toast({
      title: success ? "Configurações salvas" : "Erro ao salvar",
      description: success ? "Suas preferências foram atualizadas com sucesso." : "Não foi possível salvar suas configurações.",
      variant: success ? "default" : "destructive",
    });
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast({ title: "Senha atual obrigatória", description: "Digite sua senha atual para continuar.", variant: "destructive" });
      return;
    }

    const pwValidation = validatePassword(newPassword);
    if (!pwValidation.isValid) {
      toast({ title: "Senha fraca", description: "A senha precisa ter 8+ caracteres, 1 maiúscula e 1 número.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Senhas não coincidem", description: "A confirmação precisa ser igual à nova senha.", variant: "destructive" });
      return;
    }

    setIsChangingPassword(true);

    // Verify current password first
    const { error: verifyError } = await verifyPassword(currentPassword);
    if (verifyError) {
      setIsChangingPassword(false);
      toast({ title: "Senha atual incorreta", description: "Verifique sua senha atual e tente novamente.", variant: "destructive" });
      return;
    }

    const { error } = await updatePassword(newPassword);
    setIsChangingPassword(false);

    if (error) {
      toast({ title: "Erro ao alterar senha", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Senha alterada! 🔒", description: "Sua nova senha foi salva com sucesso." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Configurações</h1>
          <p className="text-muted-foreground">Gerencie suas preferências e informações de conta</p>
        </div>

        {/* Profile */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Perfil
              </CardTitle>
              <CardDescription>Informações básicas da sua conta</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">Nome</label>
                {profileLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Input id="name" value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))} />
                )}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  {profileLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <Input id="email" type="email" value={email} disabled className="pl-10 bg-muted" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">O email não pode ser alterado</p>
              </div>
              <Button variant="hero" size="lg" className="w-full gap-2" onClick={handleSave} disabled={isSaving || profileLoading}>
                {isSaving ? (
                  <><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />Salvando...</>
                ) : (
                  <><Save className="w-4 h-4" />Salvar Perfil</>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Security */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Segurança
              </CardTitle>
              <CardDescription>Altere sua senha de acesso</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="current-password" className="block text-sm font-medium mb-2">Senha atual</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="current-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Digite sua senha atual"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium mb-2">Nova senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Mínimo 8 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <PasswordStrengthIndicator password={newPassword} />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium mb-2">Confirmar nova senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Repita a nova senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Button variant="outline" size="lg" className="w-full gap-2" onClick={handleChangePassword} disabled={isChangingPassword || !newPassword || !currentPassword}>
                {isChangingPassword ? (
                  <><div className="w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin" />Alterando...</>
                ) : (
                  <><Lock className="w-4 h-4" />Alterar Senha</>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
